import { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { CreateNewBrandKitModal } from './components/CreateNewBrandKitModal';
import { setLogo, resetBrandKit, setBrandKit } from '../../features/brand-kit/brandKitSlice';
import { useCreateBrandKitMutation, useGetBrandKitByIdQuery, useUpdateBrandKitMutation, useUploadLogoMutation } from '../../features/brand-kit/brandKitApi';
import { motion } from "motion/react";

const svgPaths = {
  p2d557600: "M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15",
  p30d9cf00: "M17.645 5.67667C18.0856 5.23619 18.3332 4.63872 18.3332 4.01571C18.3333 3.3927 18.0859 2.79517 17.6454 2.35458C17.2049 1.91399 16.6075 1.66643 15.9845 1.66635C15.3614 1.66627 14.7639 1.91369 14.3233 2.35417L3.20167 13.4783C3.00818 13.6712 2.86509 13.9088 2.785 14.17L1.68417 17.7967C1.66263 17.8687 1.661 17.9453 1.67946 18.0182C1.69792 18.0911 1.73577 18.1577 1.789 18.2108C1.84223 18.264 1.90885 18.3017 1.9818 18.3201C2.05475 18.3384 2.1313 18.3367 2.20333 18.315L5.83083 17.215C6.09181 17.1356 6.32931 16.9934 6.5225 16.8008L17.645 5.67667Z",
  p33f6b680: "M10 15.8333L4.16667 10L10 4.16667",
  p39ee6532: "M8 14.6667C11.6819 14.6667 14.6667 11.6819 14.6667 8C14.6667 4.3181 11.6819 1.33333 8 1.33333C4.3181 1.33333 1.33333 4.3181 1.33333 8C1.33333 11.6819 4.3181 14.6667 8 14.6667Z",
  trash: "M16 6V5.2C16 4.0799 16 3.51984 15.782 3.09202C15.5903 2.71569 15.2843 2.40973 14.908 2.21799C14.4802 2 13.9201 2 12.8 2H11.2C10.0799 2 9.51984 2 9.09202 2.21799C8.71569 2.40973 8.40973 2.71569 8.21799 3.09202C8 3.51984 8 4.0799 8 5.2V6M10 11.5V16.5M14 11.5V16.5M3 6H21M19 6V17.2C19 18.8802 19 19.7202 18.673 20.362C18.3854 20.9265 17.9265 21.3854 17.362 21.673C16.7202 22 15.8802 22 14.2 22H9.8C8.11984 22 7.27976 22 6.63803 21.673C6.07354 21.3854 5.6146 20.9265 5.32698 20.362C5 19.7202 5 18.8802 5 17.2V6"
};

function Frame5() {
  const navigate = useNavigate();
  const { id } = useParams();
  const baseUrl = id ? `/brand-kit/edit/${id}` : '/brand-kit/create';

  return (
    <div className="absolute flex gap-[12px] items-center left-[22px] top-[120px] bg-gray-50/50 p-1.5 rounded-xl border border-gray-100">
      <div 
        className="relative shrink-0 min-w-[100px] h-[36px] flex items-center justify-center overflow-hidden rounded-lg group cursor-pointer"
        onClick={() => navigate(`${baseUrl}/logo`)}
      >
        <div 
            className="app-selector-indicator" 
            style={{ '--background-width': location.pathname.includes('/logo') ? '100%' : '0%' } as React.CSSProperties} 
        />
        <div className="flex items-center gap-2 relative z-10 transition-colors duration-400">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={clsx(location.pathname.includes('/logo') ? "text-[#30A84B]" : "text-[#667185]")}>
            <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
            <circle cx="9" cy="9" r="2" />
            <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
          </svg>
          <span className={clsx("font-sans font-medium text-[14px]", location.pathname.includes('/logo') ? "app-active-state" : "text-[#667185]")}>Logo</span>
        </div>
      </div>

      <div
        className="relative shrink-0 min-w-[100px] h-[36px] flex items-center justify-center overflow-hidden rounded-lg group cursor-pointer"
        onClick={() => navigate(`${baseUrl}/colors`)}
      >
        <div 
            className="app-selector-indicator" 
            style={{ '--background-width': location.pathname.includes('/colors') ? '100%' : '0%' } as React.CSSProperties} 
        />
        <div className="flex items-center gap-2 relative z-10 transition-colors duration-400">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={clsx(location.pathname.includes('/colors') ? "text-[#30A84B]" : "text-[#667185]")}>
            <circle cx="13.5" cy="6.5" r=".5" fill="currentColor" />
            <circle cx="17.5" cy="10.5" r=".5" fill="currentColor" />
            <circle cx="8.5" cy="7.5" r=".5" fill="currentColor" />
            <circle cx="6.5" cy="12.5" r=".5" fill="currentColor" />
            <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
          </svg>
          <span className={clsx("font-sans font-medium text-[14px]", location.pathname.includes('/colors') ? "app-active-state" : "text-[#667185]")}>Colours</span>
        </div>
      </div>

      <div
        className="relative shrink-0 min-w-[119px] h-[36px] flex items-center justify-center overflow-hidden rounded-lg group cursor-pointer"
        onClick={() => navigate(`${baseUrl}/typography`)}
      >
        <div 
            className="app-selector-indicator" 
            style={{ '--background-width': location.pathname.includes('/typography') ? '100%' : '0%' } as React.CSSProperties} 
        />
        <div className="flex items-center gap-2 relative z-10 transition-colors duration-400">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={clsx(location.pathname.includes('/typography') ? "text-[#30A84B]" : "text-[#667185]")}>
            <polyline points="4 7 4 4 20 4 20 7" />
            <line x1="9" x2="15" y1="20" y2="20" />
            <line x1="12" x2="12" y1="4" y2="20" />
          </svg>
          <span className={clsx("font-sans font-medium text-[14px]", location.pathname.includes('/typography') ? "app-active-state" : "text-[#667185]")}>Typography</span>
        </div>
      </div>
    </div>
  );
}

// ... Icon, Paragraph, Container, LogoUploadCard, etc. remain the same ...

// ... Card, CardContent ...

function PrimarySm() {
  const dispatch = useAppDispatch();
  const logos = useAppSelector(state => state.brandKit.logos);
  const colors = useAppSelector(state => state.brandKit.colors);
  const brandKitState = useAppSelector(state => state.brandKit);
  const isActive = logos.main || logos.light || logos.dark || colors.length > 0;
  const navigate = useNavigate();
  const { id } = useParams();
  const [createBrandKit, { isLoading: isCreating }] = useCreateBrandKitMutation();
  const [updateBrandKitMutation, { isLoading: isUpdating }] = useUpdateBrandKitMutation();

  const handleCreate = async () => {
    if (!isActive || isCreating) return;
    try {
      await createBrandKit(brandKitState).unwrap();
      dispatch(resetBrandKit());
      navigate('/brand-kit');
    } catch (error) {
      console.error('Failed to create brand kit:', error);
    }
  };

  const handleUpdate = async () => {
    if (!isActive || isUpdating || !id) return;
    try {
      await updateBrandKitMutation({ id, data: brandKitState }).unwrap();
      dispatch(resetBrandKit());
      navigate('/brand-kit');
    } catch (error) {
      console.error('Failed to update brand kit:', error);
    }
  };

  const isLoading = isCreating || isUpdating;

  return (
    <div
      className={`${isActive ? "bg-[#30a84b] cursor-pointer hover:bg-[#288e3e]" : "bg-[#bfe4c7] cursor-not-allowed"} content-stretch flex gap-[4px] h-[36px] items-center justify-center p-[12px] relative rounded-[6px] shrink-0 transition-colors`}
      data-name="Primary - Sm"
      onClick={id ? handleUpdate : handleCreate}
    >
      <p className="font-sans font-semibold leading-[normal] relative shrink-0 text-[14px] text-nowrap text-white" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
        {isLoading ? (id ? 'Updating...' : 'Creating...') : (id ? 'Update Brand Kit' : 'Create Brand Kit')}
      </p>
    </div>
  );
}


export default function CreateBrandKitLogo() {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const brandKitName = useAppSelector(state => state.brandKit.name);
  const dispatch = useAppDispatch();
  const { id } = useParams();

  // Fetch data if in edit mode
  const { data: brandKitData } = useGetBrandKitByIdQuery(id!, { skip: !id });

  useEffect(() => {
    if (brandKitData && id) {
      dispatch(setBrandKit(brandKitData));
    }
  }, [brandKitData, id, dispatch]);

  // If no ID, we are in create mode, maybe ensure clean state? 
  // The modal handles reset.

  return (
    <div className="bg-white relative size-full min-h-screen" data-name="Create Brand Kit - Logo">
      <CreateNewBrandKitModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} mode="edit" id={id} />
      <Frame2 name={brandKitName} onEdit={() => setIsEditModalOpen(true)} />
      <Frame5 />
      <CardsContainer brandKitId={id} />
    </div>
  );
}

function Icon() {
  return (
    <div className="absolute left-0 size-[16px] top-0" data-name="Icon">
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 16 16">
        <g clipPath="url(#clip0_83_354)" id="Icon">
          <path d={svgPaths.p39ee6532} id="Vector" stroke="var(--stroke-0, #2C9944)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33333" />
          <path d="M8 10.6667V8" id="Vector_2" stroke="var(--stroke-0, #2C9944)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33333" />
          <path d="M8 5.33333H8.00667" id="Vector_3" stroke="var(--stroke-0, #2C9944)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33333" />
        </g>
        <defs>
          <clipPath id="clip0_83_354">
            <rect fill="white" height="16" width="16" />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
}

function Paragraph() {
  return (
    <div className="absolute h-[39px] left-[24px] top-0 w-[334.656px]" data-name="Paragraph">
      <p className="absolute font-sans font-normal leading-[19.5px] left-0 text-[#475367] text-[12px] top-0 w-[305px]" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
        This is your main logo. Use it on light backgrounds for maximum brand impact and recognition.
      </p>
    </div>
  );
}

function Container() {
  return (
    <div className="h-[39px] relative shrink-0 w-full" data-name="Container">
      <Icon />
      <Paragraph />
    </div>
  );
}

function LogoUploadCard() {
  return (
    <div className="absolute bg-white content-stretch flex flex-col h-[72px] items-start left-0 pb-0 pt-[17px] px-[16px] top-[200px] w-[390.656px]" data-name="LogoUploadCard">
      <div aria-hidden="true" className="absolute border-[#f3f4f6] border-[1px_0px_0px] border-solid inset-0 pointer-events-none" />
      <Container />
    </div>
  );
}

function Icon1() {
  return (
    <div className="relative shrink-0 size-[24px]" data-name="Icon">
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 24 24">
        <g id="Icon">
          <path d={svgPaths.p2d557600} id="Vector" stroke="var(--stroke-0, #364153)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
          <path d="M17 8L12 3L7 8" id="Vector_2" stroke="var(--stroke-0, #364153)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
          <path d="M12 3V15" id="Vector_3" stroke="var(--stroke-0, #364153)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
        </g>
      </svg>
    </div>
  );
}

function Container1() {
  return (
    <div className="basis-0 bg-white grow min-h-px min-w-px relative rounded-[3.35544e+07px] shrink-0 w-[48px]" data-name="Container">
      <div aria-hidden="true" className="absolute border border-[#f3f4f6] border-solid inset-0 pointer-events-none rounded-[3.35544e+07px] shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)]" />
      <div className="bg-clip-padding border-0 border-[transparent] border-solid content-stretch flex items-center justify-center p-px relative size-full">
        <Icon1 />
      </div>
    </div>
  );
}

function Paragraph1() {
  return (
    <div className="h-[20px] relative shrink-0 w-full" data-name="Paragraph">
      <p className="absolute font-sans font-medium leading-[20px] left-[49.5px] text-[#101928] text-[14px] text-center text-nowrap top-[5px] tracking-[-0.1504px] translate-x-[-50%]" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
        Click to upload
      </p>
    </div>
  );
}

function Paragraph2() {
  return (
    <div className="content-stretch flex h-[16px] items-start relative shrink-0 w-full" data-name="Paragraph">
      <p className="basis-0 font-sans font-normal grow leading-[16px] min-h-px min-w-px relative shrink-0 text-[#475367] text-[12px] text-center" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
        SVG, PNG, JPG
      </p>
    </div>
  );
}

function Container2() {
  return (
    <div className="h-[40px] relative shrink-0 w-[98.313px]" data-name="Container">
      <div className="bg-clip-padding border-0 border-[transparent] border-solid content-stretch flex flex-col gap-[4px] items-start relative size-full">
        <Paragraph1 />
        <Paragraph2 />
      </div>
    </div>
  );
}

function LogoUploadCard1() {
  return (
    <div className="absolute bg-[#f9fafb] content-stretch flex flex-col gap-[16px] h-[200px] items-center left-0 px-0 py-[48px] top-0 w-[390.656px]" data-name="LogoUploadCard">
      <Container1 />
      <Container2 />
    </div>
  );
}

function UploadedState({ logo, onDelete, bgClass = "bg-[#f9fafb]" }: { logo: string, onDelete: (e: React.MouseEvent) => void, bgClass?: string }) {
  return (
    <div className={`absolute ${bgClass} content-stretch flex flex-col h-[200px] items-center justify-center left-0 top-0 w-full rounded-t-[10px] overflow-hidden group`}>
      <div className="relative w-full h-full flex items-center justify-center p-8">
        <img
          src={logo}
          alt="Uploaded logo"
          className="max-w-full max-h-[140px] object-contain"
        />
        <div className="absolute top-2 right-2">
          <div
            className="bg-white/80 hover:bg-white p-2 rounded-full cursor-pointer shadow-sm transition-all"
            onClick={onDelete}
          >
            <svg className="size-5" fill="none" viewBox="0 0 24 24">
              <path d={svgPaths.trash} stroke="#F04438" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

function LogoUploadCard2() {
  return <div className="absolute left-[378.66px] size-0 top-[12px]" data-name="LogoUploadCard" />;
}

function CardContent({ logo, onUpload, onDelete }: { logo: string | null, onUpload: () => void, onDelete: (e: React.MouseEvent) => void }) {
  return (
    <div className="absolute h-[272px] left-[2px] top-[2px] w-[390.656px]" data-name="CardContent">
      <LogoUploadCard />
      <div onClick={onUpload} className="cursor-pointer">
        {logo ? <UploadedState logo={logo} onDelete={onDelete} /> : <LogoUploadCard1 />}
      </div>
      <LogoUploadCard2 />
    </div>
  );
}

function Icon2() {
  return (
    <div className="absolute left-0 size-[16px] top-0" data-name="Icon">
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 16 16">
        <g clipPath="url(#clip0_83_354)" id="Icon">
          <path d={svgPaths.p39ee6532} id="Vector" stroke="var(--stroke-0, #2C9944)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33333" />
          <path d="M8 10.6667V8" id="Vector_2" stroke="var(--stroke-0, #2C9944)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33333" />
          <path d="M8 5.33333H8.00667" id="Vector_3" stroke="var(--stroke-0, #2C9944)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33333" />
        </g>
        <defs>
          <clipPath id="clip0_83_354">
            <rect fill="white" height="16" width="16" />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
}

function Paragraph3() {
  return (
    <div className="absolute h-[39px] left-[24px] top-0 w-[334.672px]" data-name="Paragraph">
      <p className="absolute font-sans font-normal leading-[19.5px] left-0 text-[#475367] text-[12px] top-0 w-[288px]" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
        A version of your logo optimized for light or vibrant backgrounds.
      </p>
    </div>
  );
}

function Container3() {
  return (
    <div className="h-[39px] relative shrink-0 w-full" data-name="Container">
      <Icon2 />
      <Paragraph3 />
    </div>
  );
}

function LogoUploadCard3() {
  return (
    <div className="absolute bg-white content-stretch flex flex-col h-[72px] items-start left-0 pb-0 pt-[17px] px-[16px] top-[200px] w-[390.672px]" data-name="LogoUploadCard">
      <div aria-hidden="true" className="absolute border-[#f3f4f6] border-[1px_0px_0px] border-solid inset-0 pointer-events-none" />
      <Container3 />
    </div>
  );
}

function Icon3() {
  return (
    <div className="relative shrink-0 size-[24px]" data-name="Icon">
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 24 24">
        <g id="Icon">
          <path d={svgPaths.p2d557600} id="Vector" stroke="var(--stroke-0, #364153)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
          <path d="M17 8L12 3L7 8" id="Vector_2" stroke="var(--stroke-0, #364153)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
          <path d="M12 3V15" id="Vector_3" stroke="var(--stroke-0, #364153)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
        </g>
      </svg>
    </div>
  );
}

function Container4() {
  return (
    <div className="basis-0 bg-white grow min-h-px min-w-px relative rounded-[3.35544e+07px] shrink-0 w-[48px]" data-name="Container">
      <div aria-hidden="true" className="absolute border border-[#f3f4f6] border-solid inset-0 pointer-events-none rounded-[3.35544e+07px] shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)]" />
      <div className="bg-clip-padding border-0 border-[transparent] border-solid content-stretch flex items-center justify-center p-px relative size-full">
        <Icon3 />
      </div>
    </div>
  );
}

function Paragraph4() {
  return (
    <div className="h-[20px] relative shrink-0 w-full" data-name="Paragraph">
      <p className="absolute font-sans font-medium leading-[20px] left-[49.5px] text-[#101928] text-[14px] text-center text-nowrap top-[5px] tracking-[-0.1504px] translate-x-[-50%]" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
        Click to upload
      </p>
    </div>
  );
}

function Paragraph5() {
  return (
    <div className="content-stretch flex h-[16px] items-start relative shrink-0 w-full" data-name="Paragraph">
      <p className="basis-0 font-sans font-normal grow leading-[16px] min-h-px min-w-px relative shrink-0 text-[#475367] text-[12px] text-center" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
        SVG, PNG, JPG
      </p>
    </div>
  );
}

function Container5() {
  return (
    <div className="h-[40px] relative shrink-0 w-[98.313px]" data-name="Container">
      <div className="bg-clip-padding border-0 border-[transparent] border-solid content-stretch flex flex-col gap-[4px] items-start relative size-full">
        <Paragraph4 />
        <Paragraph5 />
      </div>
    </div>
  );
}

function LogoUploadCard4() {
  return (
    <div className="absolute bg-[#f9fafb] content-stretch flex flex-col gap-[16px] h-[200px] items-center left-0 px-0 py-[48px] top-0 w-[390.672px]" data-name="LogoUploadCard">
      <Container4 />
      <Container5 />
    </div>
  );
}

function CardContent1({ logo, onUpload, onDelete }: { logo: string | null, onUpload: () => void, onDelete: (e: React.MouseEvent) => void }) {
  return (
    <div className="absolute h-[272px] left-[2px] top-[2px] w-[390.672px]" data-name="CardContent">
      <LogoUploadCard3 />
      <div onClick={onUpload} className="cursor-pointer">
        {logo ? <UploadedState logo={logo} onDelete={onDelete} /> : <LogoUploadCard4 />}
      </div>
    </div>
  );
}

function Card1({ brandKitId }: { brandKitId?: string }) {
  const dispatch = useAppDispatch();
  const logo = useAppSelector(state => state.brandKit.logos.light);
  const brandKitState = useAppSelector(state => state.brandKit);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadLogo, { isLoading }] = useUploadLogoMutation();
  const [updateBrandKitMutation] = useUpdateBrandKitMutation();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const { url } = await uploadLogo(formData).unwrap();
        dispatch(setLogo({ type: 'light', url }));

        if (brandKitId) {
          await updateBrandKitMutation({
            id: brandKitId,
            data: { ...brandKitState, logos: { ...brandKitState.logos, light: url } }
          }).unwrap();
        }
      } catch (error) {
        console.error('Failed to upload light logo:', error);
      }
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(setLogo({ type: 'light', url: null }));
    if (fileInputRef.current) fileInputRef.current.value = '';

    if (brandKitId) {
      try {
        await updateBrandKitMutation({
          id: brandKitId,
          data: { ...brandKitState, logos: { ...brandKitState.logos, light: null } }
        }).unwrap();
      } catch (error) {
        console.error('Failed to delete light logo:', error);
      }
    }
  };

  return (
    <div className={`bg-white h-[276px] relative rounded-[10px] shrink-0 w-[394.672px] shadow-sm ${isLoading ? 'opacity-70 pointer-events-none' : ''}`} data-name="Card">
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept=".jpg,.png,.svg"
        onChange={handleFileChange}
      />
      <div className="overflow-clip relative rounded-[inherit] size-full">
        {isLoading && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/50 backdrop-blur-[2px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2c9944]"></div>
          </div>
        )}
        <CardContent1 logo={logo} onUpload={() => fileInputRef.current?.click()} onDelete={handleDelete} />
      </div>
      <div aria-hidden="true" className="absolute border border-[#e4e7ec] border-solid inset-0 pointer-events-none rounded-[10px]" />
    </div>
  );
}

function Icon4() {
  return (
    <div className="absolute left-0 size-[16px] top-0" data-name="Icon">
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 16 16">
        <g clipPath="url(#clip0_83_354)" id="Icon">
          <path d={svgPaths.p39ee6532} id="Vector" stroke="var(--stroke-0, #2C9944)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33333" />
          <path d="M8 10.6667V8" id="Vector_2" stroke="var(--stroke-0, #2C9944)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33333" />
          <path d="M8 5.33333H8.00667" id="Vector_3" stroke="var(--stroke-0, #2C9944)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.33333" />
        </g>
        <defs>
          <clipPath id="clip0_83_354">
            <rect fill="white" height="16" width="16" />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
}

function Paragraph6() {
  return (
    <div className="absolute h-[39px] left-[24px] top-0 w-[334.656px]" data-name="Paragraph">
      <p className="absolute font-sans font-normal leading-[19.5px] left-0 text-[#475367] text-[12px] top-0 w-[296px]" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
        A reversed or white version of your logo, specifically designed for dark backgrounds and footer areas.
      </p>
    </div>
  );
}

function Container6() {
  return (
    <div className="h-[39px] relative shrink-0 w-full" data-name="Container">
      <Icon4 />
      <Paragraph6 />
    </div>
  );
}

function LogoUploadCard5() {
  return (
    <div className="absolute bg-white content-stretch flex flex-col h-[72px] items-start left-0 pb-0 pt-[17px] px-[16px] top-[200px] w-[390.656px]" data-name="LogoUploadCard">
      <div aria-hidden="true" className="absolute border-[#f3f4f6] border-[1px_0px_0px] border-solid inset-0 pointer-events-none" />
      <Container6 />
    </div>
  );
}

function Icon5() {
  return (
    <div className="relative shrink-0 size-[24px]" data-name="Icon">
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 24 24">
        <g id="Icon">
          <path d={svgPaths.p2d557600} id="Vector" stroke="var(--stroke-0, white)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
          <path d="M17 8L12 3L7 8" id="Vector_2" stroke="var(--stroke-0, white)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
          <path d="M12 3V15" id="Vector_3" stroke="var(--stroke-0, white)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
        </g>
      </svg>
    </div>
  );
}

function Card({ brandKitId }: { brandKitId?: string }) {
  const dispatch = useAppDispatch();
  const logo = useAppSelector(state => state.brandKit.logos.main);
  const brandKitState = useAppSelector(state => state.brandKit);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadLogo, { isLoading }] = useUploadLogoMutation();
  const [updateBrandKitMutation] = useUpdateBrandKitMutation();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const { url } = await uploadLogo(formData).unwrap();
        dispatch(setLogo({ type: 'main', url }));

        if (brandKitId) {
          await updateBrandKitMutation({
            id: brandKitId,
            data: { ...brandKitState, logos: { ...brandKitState.logos, main: url } }
          }).unwrap();
        }
      } catch (error) {
        console.error('Failed to upload main logo:', error);
      }
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(setLogo({ type: 'main', url: null }));
    if (fileInputRef.current) fileInputRef.current.value = '';

    if (brandKitId) {
      try {
        await updateBrandKitMutation({
          id: brandKitId,
          data: { ...brandKitState, logos: { ...brandKitState.logos, main: null } }
        }).unwrap();
      } catch (error) {
        console.error('Failed to delete main logo:', error);
      }
    }
  };

  return (
    <div className={`bg-white h-[276px] relative rounded-[10px] shrink-0 w-[394.656px] shadow-sm ${isLoading ? 'opacity-70 pointer-events-none' : ''}`} data-name="Card">
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept=".jpg,.png,.svg"
        onChange={handleFileChange}
      />
      <div className="overflow-clip relative rounded-[inherit] size-full">
        {isLoading && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/50 backdrop-blur-[2px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2c9944]"></div>
          </div>
        )}
        <CardContent logo={logo} onUpload={() => fileInputRef.current?.click()} onDelete={handleDelete} />
      </div>
      <div aria-hidden="true" className="absolute border border-[#e4e7ec] border-solid inset-0 pointer-events-none rounded-[10px]" />
    </div>
  );
}

function Container7() {
  return (
    <div className="basis-0 bg-[rgba(255,255,255,0.1)] grow min-h-px min-w-px relative rounded-[3.35544e+07px] shrink-0 w-[48px]" data-name="Container">
      <div className="bg-clip-padding border-0 border-[transparent] border-solid content-stretch flex items-center justify-center relative size-full">
        <Icon5 />
      </div>
    </div>
  );
}

function Paragraph7() {
  return (
    <div className="h-[20px] relative shrink-0 w-full" data-name="Paragraph">
      <p className="absolute font-sans font-medium leading-[20px] left-[49.5px] text-[14px] text-center text-nowrap text-white top-[5px] tracking-[-0.1504px] translate-x-[-50%]" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
        Click to upload
      </p>
    </div>
  );
}

function Paragraph8() {
  return (
    <div className="content-stretch flex h-[16px] items-start relative shrink-0 w-full" data-name="Paragraph">
      <p className="basis-0 font-sans font-normal grow leading-[16px] min-h-px min-w-px relative shrink-0 text-[#99a1af] text-[12px] text-center" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
        SVG, PNG, JPG
      </p>
    </div>
  );
}

function Container8() {
  return (
    <div className="h-[40px] relative shrink-0 w-[98.313px]" data-name="Container">
      <div className="bg-clip-padding border-0 border-[transparent] border-solid content-stretch flex flex-col gap-[4px] items-start relative size-full">
        <Paragraph7 />
        <Paragraph8 />
      </div>
    </div>
  );
}

function LogoUploadCard6() {
  return (
    <div className="absolute bg-[#0b1120] content-stretch flex flex-col gap-[16px] h-[200px] items-center left-0 px-0 py-[48px] top-0 w-[390.656px]" data-name="LogoUploadCard">
      <Container7 />
      <Container8 />
    </div>
  );
}

function CardContent2({ logo, onUpload, onDelete }: { logo: string | null, onUpload: () => void, onDelete: (e: React.MouseEvent) => void }) {
  return (
    <div className="absolute h-[272px] left-[2px] top-[2px] w-[390.656px]" data-name="CardContent">
      <LogoUploadCard5 />
      <div onClick={onUpload} className="cursor-pointer">
        {logo ? <UploadedState logo={logo} onDelete={onDelete} bgClass="bg-[#0b1120]" /> : <LogoUploadCard6 />}
      </div>
    </div>
  );
}



function Card2({ brandKitId }: { brandKitId?: string }) {
  const dispatch = useAppDispatch();
  const logo = useAppSelector(state => state.brandKit.logos.dark);
  const brandKitState = useAppSelector(state => state.brandKit);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadLogo, { isLoading }] = useUploadLogoMutation();
  const [updateBrandKitMutation] = useUpdateBrandKitMutation();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const { url } = await uploadLogo(formData).unwrap();
        dispatch(setLogo({ type: 'dark', url }));

        if (brandKitId) {
          await updateBrandKitMutation({
            id: brandKitId,
            data: { ...brandKitState, logos: { ...brandKitState.logos, dark: url } }
          }).unwrap();
        }
      } catch (error) {
        console.error('Failed to upload dark logo:', error);
      }
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(setLogo({ type: 'dark', url: null }));
    if (fileInputRef.current) fileInputRef.current.value = '';

    if (brandKitId) {
      try {
        await updateBrandKitMutation({
          id: brandKitId,
          data: { ...brandKitState, logos: { ...brandKitState.logos, dark: null } }
        }).unwrap();
      } catch (error) {
        console.error('Failed to delete dark logo:', error);
      }
    }
  };

  return (
    <div className={`bg-white h-[276px] relative rounded-[10px] shrink-0 w-[394.656px] shadow-sm ${isLoading ? 'opacity-70 pointer-events-none' : ''}`} data-name="Card">
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept=".jpg,.png,.svg"
        onChange={handleFileChange}
      />
      <div className="overflow-clip relative rounded-[inherit] size-full">
        {isLoading && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/50 backdrop-blur-[2px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2c9944]"></div>
          </div>
        )}
        <CardContent2 logo={logo} onUpload={() => fileInputRef.current?.click()} onDelete={handleDelete} />
      </div>
      <div aria-hidden="true" className="absolute border border-[#e4e7ec] border-solid inset-0 pointer-events-none rounded-[10px]" />
    </div>
  );
}

function CardsContainer({ brandKitId }: { brandKitId?: string }) {
  return (
    <motion.div
      className="absolute content-stretch flex gap-[24px] items-center left-[22px] top-[167px]"
      data-name="CreateBrandKitLogo"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card brandKitId={brandKitId} />
      <Card1 brandKitId={brandKitId} />
      <Card2 brandKitId={brandKitId} />
    </motion.div>
  );
}

function Frame({ onClick }: { onClick?: () => void }) {
  const navigate = useNavigate();
  return (
    <div className="relative shrink-0 size-[20px] cursor-pointer" data-name="Frame" onClick={() => onClick ? onClick() : navigate('/brand-kit')}>
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 20 20">
        <g id="Frame">
          <path d={svgPaths.p33f6b680} id="Vector" stroke="var(--stroke-0, #101928)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
          <path d="M15.8333 10H4.16667" id="Vector_2" stroke="var(--stroke-0, #101928)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
        </g>
      </svg>
    </div>
  );
}

function Frame1({ onClick }: { onClick: () => void }) {
  return (
    <div className="relative shrink-0 size-[20px] cursor-pointer" data-name="Frame" onClick={onClick}>
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 20 20">
        <g clipPath="url(#clip0_83_347)" id="Frame">
          <path d={svgPaths.p30d9cf00} id="Vector" stroke="var(--stroke-0, #344054)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
          <path d="M12.5 4.16667L15.8333 7.5" id="Vector_2" stroke="var(--stroke-0, #344054)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
        </g>
        <defs>
          <clipPath id="clip0_83_347">
            <rect fill="white" height="20" width="20" />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
}

function Frame3({ name, onEdit }: { name: string, onEdit: () => void }) {
  return (
    <div className="content-stretch flex gap-[8px] items-center relative shrink-0">
      <div className="flex flex-col font-sans font-semibold justify-center leading-[0] relative shrink-0 text-[#101928] text-[16px] text-nowrap" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
        <p className="leading-[normal]">{name}</p>
      </div>
      <Frame1 onClick={onEdit} />
    </div>
  );
}

function Frame4({ name, onEdit }: { name: string, onEdit: () => void }) {
  return (
    <div className="content-stretch flex gap-[12px] items-center relative shrink-0">
      <Frame />
      <Frame3 name={name} onEdit={onEdit} />
    </div>
  );
}

function Frame2({ name, onEdit }: { name: string, onEdit: () => void }) {
  return (
    <div className="absolute bg-white h-[72px] left-[22px] right-[22px] rounded-[8px] top-[16px]">
      <div className="content-stretch flex items-center justify-between overflow-clip px-[16px] py-[12px] relative rounded-[inherit] size-full">
        <Frame4 name={name} onEdit={onEdit} />
        <PrimarySm />
      </div>
      <div aria-hidden="true" className="absolute border-[#e4e7ec] border-[0.75px] border-solid inset-0 pointer-events-none rounded-[8px]" />
    </div>
  );
}
